import React, { useState } from 'react';
import { Spinner, Icon } from 'ui';
import { File, MoreVertical, CheckCircle, Calendar, Info } from 'react-feather';
import { snakeCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from 'components/shared/ClickAwayListener';
import { useParams } from 'react-router-dom';
import { TYPES, routes as ROUTES, TYPE_ICONS } from 'constants/config';
import request from 'utils/request_ajax';
import { Drag } from 'components/ui/Icon/Streamline';
import SessionDetails from 'components/InPersonTraining/shared/SessionDetails';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import ActionMenu from './ActionMenu';
import { updateBanner, displayBanner } from '../../../actions/journeyBuilder';
import * as actions from '../../../actions/journeyBuilder';
import ContentPicker from './ContentPicker';
import { formattedUTCDate } from '../__helpers__/journeyHelper';
import DeleteConfirmationModal from '../Modals/DeleteConfirmationModal';

const pathRoute = pathId => {
  return ROUTES.PATH_REPORTING.INDEX(pathId);
};

const lessonRoute = lessonId => {
  return ROUTES.LESSON(lessonId);
};

const eventRoute = eventId => {
  return ROUTES.IN_PERSON_TRAINING.EVENT(eventId);
};

const ArchivedStep = ({ step }) => {
  if (step.archived === true) {
    return (
      <div className="JourneyBuilderContent-eventNotification">
        <div className="JourneyBuilderContent-archivedEventsText">
          <Icon name="warning_sign" className="mr-10" size={18} />
          {t('journeys.builder.archived')}
        </div>
      </div>
    );
  }
  return null;
};

const ShowMore = ({ children }) => {
  const text = children;
  const [isShowMore, setIsShowMore] = useState(true);
  const toggleShowMore = () => {
    setIsShowMore(!isShowMore);
  };
  return (
    <p className="text" data-testid="JourneyViewerHeader-description">
      <PreserveFormatting>
        {isShowMore
          ? `${text.slice(0, 200)}${text.length > 200 ? '... ' : ' '}`
          : text}
      </PreserveFormatting>
      <>&nbsp;</>
      <button
        className="u-resetBtn"
        type="button"
        onClick={e => {
          e.preventDefault();
          toggleShowMore();
        }}
      >
        <Text
          className="mt-16 h-td-u u-inline"
          variant={TEXT_VARIANTS.BODY_LINK}
        >
          {text.length > 300 && (
            <>{isShowMore ? t('common.show_more') : t('common.show_less')}</>
          )}
        </Text>
      </button>
    </p>
  );
};

const Path = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const { steps, journeyStatus } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const dispatch = useDispatch();

  const { journeyId } = useParams();

  const deleteStepRequest = (curentJourneyId, stepId) => {
    return request.delete(
      ROUTES.JOURNEY.STEPS.RESOURCE(curentJourneyId, stepId),
      {
        journey_status: journeyStatus,
      },
    );
  };

  const toggleShowDeleteConfirmationModal = value => {
    if (value) {
      dispatch(actions.showDeleteConfirmationModal());
    } else {
      dispatch(actions.hideDeleteConfirmationModal());
    }

    setShowDeleteConfirmationModal(value);
  };

  const deleteStep = () => {
    dispatch(actions.removeStep({ index: stepIdx }));
    toggleShowDeleteConfirmationModal(false);

    deleteStepRequest(journeyId, step.stepId)
      .then(responseData => {
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: formattedUTCDate(responseData.updatedAt),
          }),
        );
        dispatch(
          actions.updateFields({
            lastUpdater: responseData.lastUpdater,
            updatedAt: responseData.updatedAt,
          }),
        );
        dispatch(
          actions.updateJourneyStatus({
            journeyStatus: responseData.journeyStatus,
          }),
        );
      })
      .fail(err => {
        console.error(err);
        dispatch(
          updateBanner({
            message: t('journeys.builder.reorder_error'),
            status: 'error',
          }),
        );
        dispatch(
          displayBanner({
            bannerDismissible: false,
          }),
        );
      });
  };

  const handleDeleteStep = () => {
    setShowActions(false);

    if (step.hasBeenPublished) {
      toggleShowDeleteConfirmationModal(true);
    } else {
      deleteStep();
    }
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  const StepIcon = () => {
    let iconName = 'path';
    if (
      step.isCert ||
      (step.type !== undefined && step.type === TYPES.CERTIFICATION)
    ) {
      iconName = TYPE_ICONS.Certification;
    }

    return <Icon className="JourneyBuilderContent-icon" name={iconName} />;
  };

  return (
    <div className="JourneyBuilderContent JourneyPath">
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          deleteStep={deleteStep}
          hideModal={() => toggleShowDeleteConfirmationModal(false)}
        />
      )}
      <div className="w-fill">
        <ArchivedStep step={step} />
        <div
          className={
            step.description
              ? 'JourneyBuilderContent-header'
              : 'JourneyBuilderContent-headerNoPad'
          }
        >
          <div className="JourneyBuilderContent-title-container">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <StepIcon />
            {step.archived === true ? (
              <div className="JourneyBuilderContent-titleNoLink">
                {step.title}
              </div>
            ) : (
              <div className="JourneyBuilderContent-title">
                <a href={pathRoute(step.contentId)}>{step.title}</a>
              </div>
            )}
            <div className="JourneyBuilderContent-actions">
              {showActions && (
                <ClickAwayListener
                  onClickAway={() => setShowActions(false)}
                  touchEvent={false}
                >
                  <ActionMenu
                    handleDelete={handleDeleteStep}
                    handleEdit={handleEditStep}
                    showEditOption={!step.hasBeenPublished}
                  />
                </ClickAwayListener>
              )}
              <MoreVertical
                className="JourneyContent-icon"
                size={20}
                onClick={() => setShowActions(true)}
              />
            </div>
          </div>
        </div>
        {step.description === null ? (
          <div className="JourneyBuilderContent-spinner">
            <Spinner />
            <span className="ml-8 c-brand-gray-darker">Loading...</span>
          </div>
        ) : (
          <>
            {!!step.description && (
              <div className="JourneyContent-content">
                <p className="JourneyContent-description f-smooth">
                  {step.description}
                </p>
              </div>
            )}
          </>
        )}
        {!!step.contents && (
          <div className="JourneyPath-content">
            {step.contents.map(content => (
              <div
                className="JourneyPath-path-container"
                key={snakeCase(content.type + content.id)}
              >
                <File className="JourneyPath-icon" size={16} />
                {step.archived === true ? (
                  <div className="JourneyPath-titleNoLink">{content.title}</div>
                ) : (
                  <div className="JourneyPath-title">
                    <a href={lessonRoute(content.id)}>{content.title}</a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Lesson = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const { steps, journeyStatus } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const deleteStepRequest = (curentJourneyId, stepId) => {
    return request.delete(
      ROUTES.JOURNEY.STEPS.RESOURCE(curentJourneyId, stepId),
      {
        journey_status: journeyStatus,
      },
    );
  };

  const toggleShowDeleteConfirmationModal = value => {
    if (value) {
      dispatch(actions.showDeleteConfirmationModal());
    } else {
      dispatch(actions.hideDeleteConfirmationModal());
    }

    setShowDeleteConfirmationModal(value);
  };

  const deleteStep = () => {
    dispatch(actions.removeStep({ index: stepIdx }));
    toggleShowDeleteConfirmationModal(false);

    deleteStepRequest(journeyId, step.stepId)
      .then(responseData => {
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: formattedUTCDate(responseData.updatedAt),
          }),
        );
        dispatch(
          actions.updateFields({
            lastUpdater: responseData.lastUpdater,
            updatedAt: responseData.updatedAt,
          }),
        );
        dispatch(
          actions.updateJourneyStatus({
            journeyStatus: responseData.journeyStatus,
          }),
        );
      })
      .fail(err => {
        console.error(err);
        dispatch(
          updateBanner({
            message: t('journeys.builder.reorder_error'),
            status: 'error',
          }),
        );
        dispatch(
          displayBanner({
            bannerDismissible: false,
          }),
        );
      });
  };

  const handleDeleteStep = () => {
    setShowActions(false);

    if (step.hasBeenPublished) {
      toggleShowDeleteConfirmationModal(true);
    } else {
      deleteStep();
    }
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  return (
    <div className="JourneyBuilderContent">
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          deleteStep={deleteStep}
          hideModal={() => toggleShowDeleteConfirmationModal(false)}
        />
      )}
      <div className="w-fill">
        <ArchivedStep step={step} />
        <div
          className={
            step.description
              ? 'JourneyBuilderContent-header'
              : 'JourneyBuilderContent-headerNoPad'
          }
        >
          <div className="JourneyBuilderContent-title-container">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <File className="JourneyBuilderContent-icon" size={20} />
            {step.archived === true ? (
              <div className="JourneyBuilderContent-titleNoLink">
                {step.title}
              </div>
            ) : (
              <div className="JourneyBuilderContent-title">
                <a href={lessonRoute(step.contentId)}>{step.title}</a>
              </div>
            )}
            <div className="JourneyBuilderContent-actions">
              {showActions && (
                <ClickAwayListener
                  onClickAway={() => setShowActions(false)}
                  touchEvent={false}
                >
                  <ActionMenu
                    handleDelete={handleDeleteStep}
                    handleEdit={handleEditStep}
                    showEditOption={!step.hasBeenPublished}
                  />
                </ClickAwayListener>
              )}
              <MoreVertical
                className="JourneyContent-icon"
                size={20}
                onClick={() => setShowActions(true)}
              />
            </div>
          </div>
        </div>
        {step.description === null ? (
          <div className="JourneyBuilderContent-spinner">
            <Spinner />
            <span className="ml-8 c-brand-gray-darker">Loading...</span>
          </div>
        ) : (
          <>
            {!!step.description && (
              <div className="JourneyContent-content">
                <p className="JourneyContent-description f-smooth">
                  {step.description}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Task = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const { steps, journeyStatus } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  // const neverPublished = !step.hasBeenPublished;

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const deleteStepRequest = (curentJourneyId, stepId) => {
    return request.delete(
      ROUTES.JOURNEY.STEPS.RESOURCE(curentJourneyId, stepId),
      {
        journey_status: journeyStatus,
      },
    );
  };

  const toggleShowDeleteConfirmationModal = value => {
    if (value) {
      dispatch(actions.showDeleteConfirmationModal());
    } else {
      dispatch(actions.hideDeleteConfirmationModal());
    }

    setShowDeleteConfirmationModal(value);
  };

  const deleteStep = () => {
    dispatch(actions.removeStep({ index: stepIdx }));
    toggleShowDeleteConfirmationModal(false);

    deleteStepRequest(journeyId, step.stepId)
      .then(responseData => {
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: formattedUTCDate(responseData.updatedAt),
          }),
        );
        dispatch(
          actions.updateFields({
            lastUpdater: responseData.lastUpdater,
            updatedAt: responseData.updatedAt,
          }),
        );
        dispatch(
          actions.updateJourneyStatus({
            journeyStatus: responseData.journeyStatus,
          }),
        );
      })
      .fail(err => {
        console.error(err);
        dispatch(
          updateBanner({
            message: t('journeys.builder.reorder_error'),
            status: 'error',
          }),
        );
        dispatch(
          displayBanner({
            bannerDismissible: false,
          }),
        );
      });
  };

  const handleDeleteStep = () => {
    setShowActions(false);

    if (step.hasBeenPublished) {
      toggleShowDeleteConfirmationModal(true);
    } else {
      deleteStep();
    }
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  function createMarkup() {
    return { __html: step.description };
  }

  return (
    <div className="JourneyBuilderContent">
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          deleteStep={deleteStep}
          hideModal={() => toggleShowDeleteConfirmationModal(false)}
        />
      )}
      <div className="w-fill">
        <div
          className={
            step.description
              ? 'JourneyBuilderContent-header'
              : 'JourneyBuilderContent-headerNoPad'
          }
        >
          <div className="JourneyBuilderContent-title-container">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <CheckCircle className="JourneyBuilderContent-icon" size={20} />
            <div className="JourneyBuilderContent-taskTitle">{step.title}</div>
            <div className="JourneyBuilderContent-actions">
              {showActions && (
                <ClickAwayListener
                  onClickAway={() => setShowActions(false)}
                  touchEvent={false}
                >
                  <ActionMenu
                    handleDelete={handleDeleteStep}
                    handleEdit={handleEditStep}
                    showEditOption={!step.hasBeenPublished}
                  />
                </ClickAwayListener>
              )}
              <MoreVertical
                className="JourneyContent-icon"
                size={20}
                onClick={() => setShowActions(true)}
              />
            </div>
          </div>
        </div>
        {step.description && (
          <div className="JourneyContent-content">
            <div
              className="JourneyTaskRender f-smooth"
              dangerouslySetInnerHTML={createMarkup()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Event = ({ step, listeners, attributes }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const { steps, journeyStatus } = useSelector(state => state);
  const stepIdx = steps.findIndex(x => x.stepId === step.stepId);

  const numberOfSessions = step.event.numberOfSessions
    ? step.event.numberOfSessions
    : step.event.number_of_sessions;

  const nextSession = step.event.nextSession
    ? step.event.nextSession
    : step.event.next_session;

  const rsvpType = step.event.rsvpType
    ? step.event.rsvpType
    : step.event.rsvp_type;

  const hasMultipleSessions = numberOfSessions > 1;
  const hasMultipleSessionsAndRsvpTypeAll =
    hasMultipleSessions && (rsvpType === 'all' || rsvpType === 0);
  const moreSessionsCount = hasMultipleSessions ? numberOfSessions - 1 : null;

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const deleteStepRequest = (curentJourneyId, stepId) => {
    return request.delete(
      ROUTES.JOURNEY.STEPS.RESOURCE(curentJourneyId, stepId),
      {
        journey_status: journeyStatus,
      },
    );
  };

  const toggleShowDeleteConfirmationModal = value => {
    if (value) {
      dispatch(actions.showDeleteConfirmationModal());
    } else {
      dispatch(actions.hideDeleteConfirmationModal());
    }

    setShowDeleteConfirmationModal(value);
  };

  const deleteStep = () => {
    dispatch(actions.removeStep({ index: stepIdx }));
    toggleShowDeleteConfirmationModal(false);

    deleteStepRequest(journeyId, step.stepId)
      .then(responseData => {
        dispatch(
          actions.updateAutosavedAt({
            autosavedAt: formattedUTCDate(responseData.updatedAt),
          }),
        );
        dispatch(
          actions.updateFields({
            lastUpdater: responseData.lastUpdater,
            updatedAt: responseData.updatedAt,
          }),
        );
        dispatch(
          actions.updateJourneyStatus({
            journeyStatus: responseData.journeyStatus,
          }),
        );
      })
      .fail(err => {
        console.error(err);
        dispatch(
          updateBanner({
            message: t('journeys.builder.reorder_error'),
            status: 'error',
          }),
        );
        dispatch(
          displayBanner({
            bannerDismissible: false,
          }),
        );
      });
  };

  const handleDeleteStep = () => {
    setShowActions(false);

    if (step.hasBeenPublished) {
      toggleShowDeleteConfirmationModal(true);
    } else {
      deleteStep();
    }
  };

  const handleEditStep = () => {
    dispatch(
      actions.updateStep({
        index: stepIdx,
        step: {
          ...step,
          editing: true,
        },
      }),
    );
  };

  const RequiredAttendance = () => {
    return (
      <div className="JourneyBuilderContent-attendanceBuilder">
        {t('journeys.builder.attendance_required')}
      </div>
    );
  };

  const NoUpcomingEvents = () => {
    if (!nextSession && !step.event.is_archived) {
      return (
        <div className="JourneyBuilderContent-eventNotification">
          <div className="JourneyBuilderContent-upcomingEventsText">
            <Info className="mr-10" size={18} />
            {t('journeys.event.no_upcoming_event')}
          </div>
        </div>
      );
    }
    return null;
  };

  const ArchivedEvents = () => {
    if (step.event.is_archived) {
      return (
        <div className="JourneyBuilderContent-eventNotification">
          <div className="JourneyBuilderContent-archivedEventsText">
            <Icon name="warning_sign" className="mr-10" size={18} />
            {t('journeys.event.archived_event')}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="JourneyBuilderContent">
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          deleteStep={deleteStep}
          hideModal={() => toggleShowDeleteConfirmationModal(false)}
        />
      )}
      <div className="w-fill">
        <ArchivedEvents />
        <NoUpcomingEvents />
        <div
          className={
            step.description
              ? 'JourneyBuilderContent-header'
              : 'JourneyBuilderContent-headerNoPad'
          }
        >
          <div className="JourneyBuilderContent-title-container">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <Calendar
              className="JourneyBuilderContent-calendarIcon"
              size={28}
            />
            <div className="JourneyBuilderContent-title">
              <a href={eventRoute(step.contentId)}>{step.title}</a>
            </div>
            <div className="JourneyBuilderContent-actions">
              <RequiredAttendance />
              {showActions && (
                <ClickAwayListener
                  onClickAway={() => setShowActions(false)}
                  touchEvent={false}
                >
                  <ActionMenu
                    handleDelete={handleDeleteStep}
                    handleEdit={handleEditStep}
                    showEditOption={!step.hasBeenPublished}
                  />
                </ClickAwayListener>
              )}
              <MoreVertical
                className="JourneyContent-iconEvents"
                size={20}
                onClick={() => setShowActions(true)}
              />
            </div>
          </div>
        </div>
        {step.description === null ? (
          <div className="JourneyBuilderContent-spinner">
            <Spinner />
            <span className="ml-8 c-brand-gray-darker">Loading...</span>
          </div>
        ) : (
          <>
            <div className="JourneyContent-content">
              {!!step.description && (
                <p className="JourneyContent-description f-smooth">
                  <ShowMore>{step.description}</ShowMore>
                </p>
              )}
              <SessionDetails
                className="JourneyBuilderContent-eventDetails"
                eventId={step.event.id}
                hasMultipleSessions={hasMultipleSessions}
                moreSessionsCount={moreSessionsCount}
                rsvpType={hasMultipleSessionsAndRsvpTypeAll ? 'all' : 'single'}
                session={nextSession || step.event.last_session}
                sessionCount={step.event.numberOfSessions}
                showRegistrationCap={false}
              />
              {hasMultipleSessionsAndRsvpTypeAll && (
                <div className="JourneyBuilderContent-eventFooter">
                  <Info className="c-midnight-50 mr-10" size={18} />
                  <p>{t('journeys.event.info_to_complete_step')}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ContentRenderer = ({
  type,
  step,
  idx,
  spacerHeight,
  listeners,
  attributes,
}) => {
  switch (type) {
    case 'Lesson':
      return step.contentId && !step.editing ? (
        <Lesson step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Path':
      return step.contentId && !step.editing ? (
        <Path step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Task':
      return step.contentId && !step.editing ? (
        <Task step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Event':
      return step.contentId && !step.editing ? (
        <Event step={step} listeners={listeners} attributes={attributes} />
      ) : (
        <ContentPicker
          step={step}
          idx={idx}
          listeners={listeners}
          attributes={attributes}
        />
      );
    case 'Spacer':
      return (
        <div
          className="JourneyBuilderContent-spacer"
          style={{ height: spacerHeight }}
        >
          <div className="JourneyBuilderContent-spacerInner" />
        </div>
      );
    default:
      return null;
  }
};

export default ContentRenderer;
